<template>
  <b-sidebar
    v-model="show"
    id="packages-overlay-sidebar"
    width="70%"
    right
    shadow
    backdrop
    no-header
    footer-class="border-top"
    @hidden="$emit('hidden')"
    @shown="OnShown"
  >
    <!-- Header -->
    <div class="border-bottom pb-3 pt-3 px-3 bg-primary text-white">
      <div class="d-flex align-items-center justify-content-between w-100">
        <b-button variant="outline-light" size="sm" @click="show = false">
          <i class="fa fa-arrow-left mr-1"></i> Close
        </b-button>
        <div class="d-flex align-items-center">
          <h4 class="mb-0 mr-3">Pallets / Packages</h4>
          <span v-if="packages.length > 0" class="badge badge-light">{{ packages.length }} unit(s)</span>
        </div>
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-2 small text-white-50">Unit Type:</label>
          <b-form-select v-model="globalUnitType" size="sm" style="width: 140px">
            <option value="Pallet">Pallet</option>
            <option value="Case">Case</option>
            <option value="Package">Package</option>
          </b-form-select>
        </div>
      </div>
    </div>

    <!-- Body: two-column layout -->
    <div class="d-flex" style="min-height: 100%">

      <!-- Main content area -->
      <div class="p-3 flex-grow-1" style="overflow-y: auto">

        <!-- Error alert -->
        <b-alert v-if="errorMessage" show dismissible variant="danger" @dismissed="errorMessage = null">
          <i class="fa fa-exclamation-triangle mr-1"></i> {{ errorMessage }}
        </b-alert>

        <!-- Loading state -->
        <div v-if="loadingPackages" class="text-center py-5">
          <b-spinner></b-spinner>
          <p class="mt-2 text-muted">Loading packages...</p>
        </div>

        <!-- Empty state -->
        <div v-else-if="packages.length === 0" class="text-center py-5 text-muted">
          <i class="fa fa-archive fa-3x mb-3"></i>
          <h5>No packages yet specified.</h5>
          <p>Click <strong>Auto-Generate from Allocations</strong> to generate pallets/packages from the TPL allocations,<br>or use the <strong>Add Package</strong> button to create one manually.</p>
        </div>

        <!-- Package cards -->
        <div v-else>
          <div
            v-for="(pkg, pkgIndex) in packages"
            :key="pkgIndex"
            class="card mb-3"
          >
            <!-- Package Card Header -->
            <div class="card-header py-2" style="background-color: #737b87; padding: 12px 13px;">
              <div class="d-flex align-items-center justify-content-between w-100">
                <!-- Left: package info labels -->
                <div class="d-flex align-items-center flex-nowrap" style="overflow: hidden; min-width: 0">
                  <span class="text-white-50 small mr-1 flex-shrink-0">#{{ pkgIndex + 1 }}</span>
                  <span class="mx-2 text-white-50 flex-shrink-0">|</span>

                  <span :class="unitTypeBadgeClass(pkg.unitType)" class="mr-3 flex-shrink-0" style="font-size: 0.85rem; padding: 0.3em 0.6em">
                    {{ pkg.unitType }}
                  </span>

                  <span v-if="!pkg._editing" class="text-white-50 small mr-1 flex-shrink-0">ID:</span>
                  <span v-if="!pkg._editing" class="text-white font-weight-bold mr-3 flex-shrink-0" style="font-size: 1rem">
                    {{ pkg.packageCode || '(no code)' }}
                  </span>
                  <b-input-group v-else size="sm" prepend="ID" class="mr-3 flex-shrink-0" style="width: 260px">
                    <b-form-input v-model="pkg.packageCode" placeholder="LPN / SSCC" />
                    <b-input-group-append>
                      <b-button
                        variant="outline-secondary"
                        size="sm"
                        :disabled="generatingCodeIndex === pkgIndex"
                        v-b-tooltip.hover
                        title="Generate SSCC code"
                        @click="GeneratePackageCode(pkgIndex)"
                      >
                        <i v-if="generatingCodeIndex === pkgIndex" class="fa fa-spinner fa-spin"></i>
                        <i v-else class="fa fa-magic"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>

                  <span v-if="!pkg._editing && pkg.trackingNumber" class="text-white-50 small mr-1 flex-shrink-0">Tracking:</span>
                  <span v-if="!pkg._editing && pkg.trackingNumber" class="text-white mr-3 flex-shrink-0">{{ pkg.trackingNumber }}</span>
                  <b-input-group v-if="pkg._editing" size="sm" prepend="Tracking" class="mr-3 flex-shrink-0" style="width: 220px">
                    <b-form-input v-model="pkg.trackingNumber" placeholder="Tracking number" />
                  </b-input-group>

                </div>

                <!-- Right: qty total + action buttons -->
                <div class="d-flex align-items-center ml-3" style="flex-shrink: 0">
                  <span class="text-white-50 small mr-3">
                    {{ (pkg.lineItems || []).reduce(function(s, li) { return s + (li.quantity || 0); }, 0) }} unit(s)
                  </span>
                  <b-button
                    size="sm"
                    :variant="pkg._editing ? 'success' : 'outline-light'"
                    class="mr-1"
                    @click="toggleEditPackage(pkgIndex)"
                  >
                    <i :class="pkg._editing ? 'fa fa-check' : 'fa fa-pencil'"></i>
                    {{ pkg._editing ? 'Done' : 'Edit' }}
                  </b-button>
                  <b-button size="sm" variant="outline-danger" @click="removePackage(pkgIndex)">
                    <i class="fa fa-trash"></i>
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Line Items Table -->
            <div class="card-body p-0">
              <table class="table table-sm table-hover mb-0">
                <thead style="background-color: #e8ecf0;">
                  <tr>
                    <th class="text-muted" style="font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em">SKU</th>
                    <th class="text-muted text-center" style="font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em">Qty</th>
                    <th class="text-muted" style="font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em">Lot #</th>
                    <th v-if="pkg.unitType !== 'Pallet'" class="text-muted" style="font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em">LPN</th>
                    <th class="text-muted" style="font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em">Expires</th>
                    <th v-if="pkg.unitType !== 'Pallet'" class="text-muted" style="font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em">Location</th>
                    <th v-if="pkg._editing" style="width: 50px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!pkg.lineItems || pkg.lineItems.length === 0">
                    <td :colspan="pkg._editing ? (pkg.unitType === 'Pallet' ? 5 : 7) : (pkg.unitType === 'Pallet' ? 4 : 6)" class="text-center text-muted py-2">
                      No line items
                    </td>
                  </tr>
                  <tr v-for="(li, liIndex) in pkg.lineItems" :key="liIndex">
                    <td>
                      <b-form-input v-if="pkg._editing" v-model="li.sku" size="sm" class="w-auto" style="min-width:100px" />
                      <template v-else>
                        <strong>{{ li.sku }}</strong>
                        <div v-if="getItemName(li.sku)" class="small text-muted">{{ getItemName(li.sku) }}</div>
                      </template>
                    </td>
                    <td class="text-center">
                      <b-form-input
                        v-if="pkg._editing"
                        v-model.number="li.quantity"
                        type="number"
                        min="0"
                        step="1"
                        size="sm"
                        class="text-center"
                        style="width: 80px; margin: 0 auto"
                      />
                      <span v-else>{{ li.quantity }}</span>
                    </td>
                    <td>
                      <b-form-input v-if="pkg._editing" v-model="li.lotNumber" size="sm" class="w-auto" />
                      <span v-else>{{ li.lotNumber || '-' }}</span>
                    </td>
                    <td v-if="pkg.unitType !== 'Pallet'">
                      <b-form-input v-if="pkg._editing" v-model="li.lpn" size="sm" class="w-auto" />
                      <span v-else>{{ li.lpn || '-' }}</span>
                    </td>
                    <td>
                      <span v-if="li.expiresDate">{{ li.expiresDate }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td v-if="pkg.unitType !== 'Pallet'">
                      <span>{{ li.warehouseLocationCode || '-' }}</span>
                    </td>
                    <td v-if="pkg._editing" class="text-center">
                      <b-button size="sm" variant="outline-danger" @click="removeLineItem(pkgIndex, liIndex)">
                        <i class="fa fa-times"></i>
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Add line item row -->
              <div v-if="pkg._editing" class="px-3 py-2 bg-light border-top">
                <b-button size="sm" variant="outline-success" @click="addLineItem(pkgIndex)">
                  <i class="fa fa-plus mr-1"></i> Add Line Item
                </b-button>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Right sidebar panel -->
      <div class="border-left bg-light p-3" style="width: 25%; min-width: 220px; flex-shrink: 0">
        <b-button
          block
          variant="primary"
          class="mb-2"
          :disabled="loadingAutogenerate"
          @click="AutoGenerate"
        >
          <i v-if="loadingAutogenerate" class="fa fa-spinner fa-spin mr-1"></i>
          <i v-else class="fa fa-magic mr-1"></i>
          Auto-Generate from Allocations
        </b-button>
        <b-button
          block
          variant="success"
          class="mb-3"
          :disabled="loadingSave"
          @click="Save"
        >
          <i v-if="loadingSave" class="fa fa-spinner fa-spin mr-1"></i>
          <i v-else class="fa fa-save mr-1"></i>
          Save Shipping Units
        </b-button>

        <hr class="mt-0" />

        <b-button block variant="outline-secondary" size="sm" @click="addPackage">
          <i class="fa fa-plus mr-1"></i> Add Package
        </b-button>

        <div v-if="packages.length > 0" class="mt-3">
          <p class="text-muted small mb-1"><strong>Summary</strong></p>
          <p class="text-muted small mb-1">{{ packages.length }} unit(s)</p>
          <p class="text-muted small mb-0">{{ totalLineItemCount }} line item(s)</p>
        </div>
      </div>

    </div>
  </b-sidebar>
</template>

<script>
export default {
  name: 'PackagesOverlay',

  model: {
    prop: 'value',
    event: 'input'
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    manualShipmentId: {
      type: String,
      required: true
    },
    defaultUnitType: {
      type: String,
      default: null
    },
    items: {
      type: Array,
      default: function() { return []; }
    }
  },

  data: function() {
    return {
      show: false,
      packages: [],
      globalUnitType: this.defaultUnitType || 'Pallet',
      loadingPackages: false,
      loadingAutogenerate: false,
      loadingSave: false,
      generatingCodeIndex: null,
      errorMessage: null
    };
  },

  computed: {
    totalLineItemCount: function() {
      return this.packages.reduce(function(sum, pkg) {
        return sum + ((pkg.lineItems && pkg.lineItems.length) || 0);
      }, 0);
    }
  },

  watch: {
    value: function(val) {
      this.show = val;
    },
    show: function(val) {
      this.$emit('input', val);
    }
  },

  methods: {
    getItemName: function(sku) {
      var match = (this.items || []).find(function(i) { return i.item === sku; });
      return match ? match.description : null;
    },

    OnShown: function() {
      this.LoadPackages();
    },

    LoadPackages: function() {
      var self = this;
      this.loadingPackages = true;
      this.errorMessage = null;

      this.$http
        .get('/api/common/manualShipments/' + this.manualShipmentId + '/packages')
        .then(function(resp) {
          self.packages = self.PreparePackages(resp.data.packages || []);
        })
        .catch(function(err) {
          var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Failed to load packages';
          self.errorMessage = msg;
          console.error('Error loading packages', err);
        })
        .finally(function() {
          self.loadingPackages = false;
        });
    },

    AutoGenerate: function() {
      var self = this;
      this.loadingAutogenerate = true;
      this.errorMessage = null;

      this.packages = [];
      this.$http
        .post('/api/common/manualShipments/' + this.manualShipmentId + '/packages/autogenerate')
        .then(function(resp) {
          self.packages = self.PreparePackages(resp.data.packages || []);
          if (self.packages.length === 0) {
            self.errorMessage = 'AutoGenerate returned no packages. Ensure the order has been allocated in the warehouse system.';
          }
        })
        .catch(function(err) {
          var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'AutoGenerate failed';
          self.errorMessage = msg;
          console.error('AutoGenerate error', err);
        })
        .finally(function() {
          self.loadingAutogenerate = false;
        });
    },

    Save: function() {
      var self = this;
      this.loadingSave = true;
      this.errorMessage = null;

      var payload = {
        manualShipmentId: this.manualShipmentId,
        packages: this.packages.map(function(pkg) {
          return self.StripUiFields(pkg);
        })
      };

      this.$http
        .post('/api/common/manualShipments/' + this.manualShipmentId + '/packages/save', payload)
        .then(function(resp) {
          var data = resp.data;
          var msg = 'Saved! Created: ' + data.created + ', Updated: ' + data.updated + ', Deleted: ' + data.deleted + '.';
          self.$swal('Saved', msg, 'success');
          // Reload to get server-assigned IDs
          self.LoadPackages();
        })
        .catch(function(err) {
          var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Save failed';
          self.errorMessage = msg;
          console.error('Save error', err);
        })
        .finally(function() {
          self.loadingSave = false;
        });
    },

    // Adds a _editing flag (UI-only) to each package and its line items
    PreparePackages: function(rawPackages) {
      return (rawPackages || []).map(function(pkg) {
        return Object.assign({}, pkg, {
          _editing: false,
          lineItems: (pkg.lineItems || []).map(function(li) {
            return Object.assign({}, li);
          })
        });
      });
    },

    // Strips UI-only fields before sending to API
    StripUiFields: function(pkg) {
      var clean = Object.assign({}, pkg);
      delete clean._editing;
      clean.lineItems = (pkg.lineItems || []).map(function(li) {
        return Object.assign({}, li);
      });
      return clean;
    },

    toggleEditPackage: function(index) {
      this.packages[index]._editing = !this.packages[index]._editing;
      this.$set(this.packages, index, this.packages[index]);
    },

    addPackage: function() {
      this.packages.push({
        id: null,
        unitType: this.globalUnitType,
        packageCode: '',
        trackingNumber: '',
        packagingWeight: null,
        netWeight: null,
        grossWeight: null,
        dimensions: null,
        generatedLabelUrl: null,
        labelGeneratedDateTimeUtc: null,
        lineItems: [],
        _editing: true
      });
    },

    removePackage: function(index) {
      this.packages.splice(index, 1);
    },

    addLineItem: function(pkgIndex) {
      this.packages[pkgIndex].lineItems.push({
        id: null,
        brandProductId: null,
        sku: '',
        lotNumber: '',
        lpn: '',
        expiresDate: null,
        warehouseLocationCode: '',
        warehouseProductLocationId: null,
        tplReceiveItemId: null,
        quantity: 1,
        serialNumbers: []
      });
      this.$set(this.packages, pkgIndex, this.packages[pkgIndex]);
    },

    removeLineItem: function(pkgIndex, liIndex) {
      this.packages[pkgIndex].lineItems.splice(liIndex, 1);
      this.$set(this.packages, pkgIndex, this.packages[pkgIndex]);
    },

    GeneratePackageCode: function(pkgIndex) {
      var self = this;
      this.generatingCodeIndex = pkgIndex;

      this.$http
        .post('/api/common/manualShipments/' + this.manualShipmentId + '/packages/generate-code')
        .then(function(resp) {
          self.packages[pkgIndex].packageCode = resp.data.packageCode;
          self.$set(self.packages, pkgIndex, self.packages[pkgIndex]);
        })
        .catch(function(err) {
          var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Failed to generate code';
          self.errorMessage = msg;
        })
        .finally(function() {
          self.generatingCodeIndex = null;
        });
    },

    unitTypeBadgeClass: function(unitType) {
      if (unitType === 'Pallet') return 'badge badge-primary';
      if (unitType === 'Case') return 'badge badge-success';
      return 'badge badge-secondary';
    }
  }
};
</script>
